import React from "react";
import { useInView } from "react-intersection-observer";
import { useTheme } from "@/context/theme-context";
import { motion } from "framer-motion";

type TimelineElementProps = {
  item: {
    title: string;
    location: string;
    description: string;
    date: string;
    icon: React.ReactNode;
  };
  index: number;
};

export default function TimelineElement({ item, index }: TimelineElementProps) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: "-40px",
  });
  const { theme } = useTheme();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.55,
        delay: index * 0.06,
        ease: [0.25, 1, 0.5, 1],
      }}
      className="relative flex gap-5 sm:gap-7"
    >
      {/* Timeline spine + dot */}
      <div className="flex flex-col items-center shrink-0 pt-1">
        {/* Icon circle */}
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0
            bg-[var(--color-surface-raised)]
            border border-[var(--color-border)]
            text-[var(--color-text-secondary)]
            text-base"
          aria-hidden="true"
        >
          {item.icon}
        </div>
        {/* Spine line — rendered for all but last via CSS in parent */}
        <div className="flex-1 w-px bg-[var(--color-border)] mt-2 timeline-spine" />
      </div>

      {/* Content */}
      <div className="pb-10 min-w-0 flex-1">
        <div className="flex flex-col sm:flex-row sm:items-baseline
          sm:justify-between gap-1 sm:gap-4 mb-2">
          <h3 className="font-sans font-semibold text-[0.95rem] leading-snug
            text-[var(--color-text-primary)]">
            {item.title}
          </h3>
          <time
            className="text-xs font-mono tabular shrink-0
              text-[var(--color-text-tertiary)] whitespace-nowrap"
          >
            {item.date}
          </time>
        </div>

        <p className="text-xs font-medium tracking-wide
          text-[var(--color-brand)] mb-2 uppercase">
          {item.location}
        </p>

        <p className="font-serif text-sm leading-relaxed
          text-[var(--color-text-secondary)]">
          {item.description}
        </p>
      </div>
    </motion.div>
  );
}
